'use client';

import { useId } from 'react';
import { FeatureLink } from './FeatureLink';
import { PlusCircle } from './icons/PlusCircle';
import type { MeasureFeature } from './measureMedia';
import styles from './MeasureAccordion.module.css';

export type { MeasureFeature } from './measureMedia';

export function MeasureAccordion({
  features,
  openId,
  onOpenChange,
  onIntent,
}: {
  features: MeasureFeature[];
  openId: string | null;
  onOpenChange: (id: string | null) => void;
  onIntent?: (id: string) => void;
}) {
  const baseId = useId();

  return (
    <div className={styles.wrap}>
      {features.map((feature) => {
        const isOpen = openId === feature.id;
        const panelId = `${baseId}-panel-${feature.id}`;
        const triggerId = `${baseId}-trigger-${feature.id}`;
        const intent = () => onIntent?.(feature.id);

        return (
          <div
            key={feature.id}
            className={styles.item}
            data-state={isOpen ? 'open' : 'closed'}
          >
            <button
              type="button"
              id={triggerId}
              className={styles.trigger}
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => onOpenChange(isOpen ? null : feature.id)}
              onPointerEnter={intent}
              onPointerDown={intent}
              onFocus={intent}
            >
              <span className={styles.labelRow}>
                <span className={styles.indicatorSlot}>
                  <span className={styles.indicator} aria-hidden="true" />
                </span>
                <span className={styles.label}>{feature.title}</span>
              </span>
              <span className={styles.icons} aria-hidden>
                <PlusCircle className={styles.icon} />
              </span>
            </button>
            <div
              id={panelId}
              role="region"
              aria-labelledby={triggerId}
              className={styles.panel}
              inert={!isOpen}
            >
              <div className={styles.panelInner}>
                <p className={styles.body}>{feature.body}</p>
                {feature.link ? (
                  <p className={styles.linkRow}>
                    <FeatureLink href={feature.link.href}>
                      {feature.link.label}
                    </FeatureLink>
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
